// Unless explicitly stated otherwise all files in this repository are licensed under the MIT License.
// This product includes software developed at Datadog (https://www.datadoghq.com/).
// Copyright 2019-Present Datadog, Inc.

import type { PluginOptions, GetPlugins } from '@dd/core/types';
import { InjectPosition } from '@dd/core/types';
import path from 'path';

import type { RumInitConfiguration, RumPublicApi } from './browserSdkTypes';
import { CONFIG_KEY, PLUGIN_NAME } from './constants';
import { getSourceCodeContextSnippet } from './getSourceCodeContextSnippet';
import { getPrivacyPlugin } from './privacy';
import { getInjectionValue } from './sdk';
import type { RumOptions, RumOptionsWithSdk } from './types';
import { validateOptions } from './validate';

export { CONFIG_KEY, PLUGIN_NAME };

export const helpers = {
    // Add the helpers you'd like to expose here.
};

export type types = {
    RumOptions: RumOptions;
    RumPublicApi: RumPublicApi;
    RumInitConfiguration: RumInitConfiguration;
};

export const getPlugins: GetPlugins = ({ options, context }) => {
    const log = context.getLogger(PLUGIN_NAME);
    const validatedOptions = validateOptions(options, log);
    const plugins: PluginOptions[] = [];
    const sourceCodeContext = validatedOptions.sourceCodeContext;

    if (sourceCodeContext) {
        context.inject({
            type: 'code',
            position: InjectPosition.BEFORE,
            injectIntoAllChunks: true,
            value: (chunk) => {
                // The debug_id is embedded directly in the returned code (see
                // getSourceCodeContextSnippet.ts); error-tracking reads it back out of the
                // built file's content at upload time, so it doesn't need to be tracked here.
                const { code } = getSourceCodeContextSnippet(sourceCodeContext, chunk);
                return code;
            },
        });
    }

    // NOTE: These files are built from "@dd/tools/rollupConfig.mjs" and available in the distributed package.
    if (validatedOptions.sdk) {
        // Inject the SDK from the CDN.
        context.inject({
            type: 'file',
            // Using MIDDLE otherwise it's not executed in context.
            position: InjectPosition.MIDDLE,
            // This file is being built alongside the bundler plugin.
            value: path.join(__dirname, './rum-browser-sdk.js'),
        });

        // Inject the SDK Initialization.
        context.inject({
            type: 'code',
            position: InjectPosition.MIDDLE,
            value: getInjectionValue(validatedOptions as RumOptionsWithSdk, context),
        });
    }

    if (validatedOptions.privacy) {
        // Add the privacy plugin.
        context.inject({
            type: 'file',
            position: InjectPosition.BEFORE,
            value: path.join(__dirname, './privacy-helpers.js'),
        });
        const privacyPlugin = getPrivacyPlugin(validatedOptions.privacy, context);
        plugins.push(privacyPlugin);
    }

    return plugins;
};
